import { useMemo } from "react";
import type { Locale } from "date-fns";
import { useTranslation } from "react-i18next";

import { useCurrency } from "@/contexts/CurrencyContext";

import { dateFnsLocaleFor } from "./date-fns-locales";
import {
  activeLanguage,
  formatCompact,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  formatPercent,
  formatRelative,
  formatTime,
  toIntlTag,
  type DateFormat,
  type DateInput,
} from "./format";

/**
 * Components' entry point to formatting: the pure functions in `format.ts`
 * with the locale and the workspace currency already bound.
 *
 * The two settings are deliberately kept apart:
 *  - GROUPING, DECIMAL SEPARATOR, DATE ORDER come from the USER's language
 *    (`i18n.language`) — how this person reads numbers.
 *  - The CURRENCY CODE comes from the WORKSPACE (`CurrencyContext`) — what the
 *    money actually is. A German rep looking at a USD pipeline must see
 *    "1.234,00 $", not "1.234,00 €".
 *
 * Re-binding is memoized on those two values, so a component holding the result
 * in a dependency array does not re-render on every parent render — but the
 * identity DOES change on a language switch, which is what makes every cell
 * re-render into the new locale.
 */
export interface BoundFormatters {
  /** Current language code, e.g. "de". */
  language: string;
  /** BCP-47 tag Intl is formatting with, e.g. "de-DE". */
  locale: string;
  /** Workspace currency code, e.g. "EUR". */
  currencyCode: string;
  /** date-fns locale for token patterns and the calendar components. */
  dateFnsLocale: Locale;
  formatDate: (date: DateInput, style?: DateFormat) => string;
  formatDateTime: (date: DateInput, style?: DateFormat) => string;
  formatTime: (date: DateInput) => string;
  formatRelative: (date: DateInput, now?: DateInput) => string;
  formatNumber: (value: number | null | undefined, options?: Intl.NumberFormatOptions) => string;
  formatCompact: (value: number | null | undefined, options?: Intl.NumberFormatOptions) => string;
  formatPercent: (ratio: number | null | undefined, options?: Intl.NumberFormatOptions) => string;
  /**
   * Money in the workspace currency. `options.currency` overrides the code for
   * the rare cell that carries its own (a multi-currency deal amount).
   */
  formatCurrency: (
    amount: number | null | undefined,
    options?: Intl.NumberFormatOptions & { currency?: string },
  ) => string;
}

export function useFormatters(): BoundFormatters {
  const { i18n } = useTranslation();
  const { currency } = useCurrency();

  // `i18n?.language` rather than `i18n.language`: component tests commonly mock
  // react-i18next with only `t`, and formatting must survive that.
  const language = i18n?.language ?? activeLanguage();
  const currencyCode = currency.code;

  return useMemo<BoundFormatters>(
    () => ({
      language,
      locale: toIntlTag(language),
      currencyCode,
      dateFnsLocale: dateFnsLocaleFor(language),
      formatDate: (date, style) => formatDate(date, language, style),
      formatDateTime: (date, style) => formatDateTime(date, language, style),
      formatTime: date => formatTime(date, language),
      formatRelative: (date, now) => formatRelative(date, language, now),
      formatNumber: (value, options) => formatNumber(value, language, options),
      formatCompact: (value, options) => formatCompact(value, language, options),
      formatPercent: (ratio, options) => formatPercent(ratio, language, options),
      formatCurrency: (amount, options) => {
        const { currency: override, ...rest } = options ?? {};
        return formatCurrency(amount, language, override ?? currencyCode, rest);
      },
    }),
    [language, currencyCode],
  );
}
